import { motion } from "framer-motion";
import { useState } from "react";
import {
  MessageSquare,
  Search,
  Handshake,
  FileCheck,
  Key,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const journeySteps = [
  {
    number: "01",
    icon: MessageSquare,
    title: "Discovery & Alignment",
    subtitle: "Understanding your vision",
    description:
      "We begin with a confidential consultation to understand your objectives — whether capital growth, lifestyle acquisition, residency, or portfolio diversification. Your goals shape every recommendation.",
    deliverables: [
      "Investment profile assessment",
      "Risk & timeline alignment",
      "Budget & jurisdiction mapping",
    ],
    timeline: "Day 1–3",
  },
  {
    number: "02",
    icon: Search,
    title: "Market Intelligence & Sourcing",
    subtitle: "Data-driven property selection",
    description:
      "Our team analyzes markets across 14 global destinations, combining on-the-ground intelligence with macro-economic data. We shortlist properties — including off-market opportunities — that precisely match your criteria.",
    deliverables: [
      "Curated property shortlist",
      "Market comparison report",
      "Off-market opportunity access",
    ],
    timeline: "Week 1–3",
  },
  {
    number: "03",
    icon: Handshake,
    title: "Local Partner Introduction",
    subtitle: "Best-in-class local expertise",
    description:
      "We connect you with our vetted local brokerage partners — firms like Sotheby's, Christie's, and leading independents — who provide intimate market knowledge, arrange private viewings, and navigate local regulations.",
    deliverables: [
      "Dedicated local agent assignment",
      "Private property viewings",
      "Neighborhood & lifestyle briefing",
    ],
    timeline: "Week 2–4",
  },
  {
    number: "04",
    icon: FileCheck,
    title: "Due Diligence & Negotiation",
    subtitle: "Protecting your investment",
    description:
      "Our fiduciary team coordinates legal review, valuation, structural surveys, and tax structuring. We negotiate on your behalf, ensuring optimal terms and complete transparency at every stage.",
    deliverables: [
      "Legal & regulatory compliance",
      "Independent valuation",
      "Tax optimization strategy",
    ],
    timeline: "Week 3–8",
  },
  {
    number: "05",
    icon: Key,
    title: "Acquisition & Beyond",
    subtitle: "From signing to stewardship",
    description:
      "We manage the closing process end-to-end — from contract execution to key handover. Post-acquisition, we provide ongoing support including property management, rental optimization, and portfolio review.",
    deliverables: [
      "Seamless transaction closing",
      "Property management setup",
      "Annual portfolio review",
    ],
    timeline: "Week 6–12",
  },
];

const ClientJourneySection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const current = journeySteps[activeStep];

  return (
    <section id="journey" className="py-24 lg:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 lg:mb-20"
        >
          <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4">
            Your Acquisition Pathway
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-6">
            From First Inquiry to
            <span className="block italic font-medium">Key Handover</span>
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            A structured, transparent process refined over nearly three decades — 
            ensuring every acquisition is executed with precision and complete discretion.
          </p>
        </motion.div>

        {/* Journey Steps — Desktop: horizontal stepper + detail, Mobile: vertical accordion */}
        <div className="max-w-6xl mx-auto">
          {/* Step indicators */}
          <div className="hidden lg:flex items-center mb-12">
            {journeySteps.map((step, index) => (
              <div key={step.number} className="flex-1 flex items-center">
                <button
                  onClick={() => setActiveStep(index)}
                  className={`flex items-center gap-3 transition-all duration-300 group w-full ${
                    index === activeStep ? "opacity-100" : "opacity-50 hover:opacity-80"
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      index === activeStep
                        ? "bg-primary text-primary-foreground shadow-gold"
                        : index < activeStep
                        ? "bg-primary/20 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {index < activeStep ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <step.icon className="h-5 w-5" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="font-body text-xs text-muted-foreground uppercase tracking-wider">
                      Step {step.number}
                    </p>
                    <p
                      className={`font-display text-sm ${
                        index === activeStep ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {step.title}
                    </p>
                  </div>
                </button>
                {index < journeySteps.length - 1 && (
                  <div
                    className={`h-px flex-1 mx-4 transition-colors duration-300 ${
                      index < activeStep ? "bg-primary/40" : "bg-border"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Active step detail — Desktop */}
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="hidden lg:grid lg:grid-cols-2 gap-12 bg-card border border-border/50 rounded-2xl p-10 lg:p-12"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <current.icon className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-primary">
                    {current.subtitle}
                  </p>
                  <h3 className="font-display text-2xl lg:text-3xl text-foreground">
                    {current.title}
                  </h3>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {current.description}
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/20">
                <span className="font-body text-xs text-primary font-medium uppercase tracking-wider">
                  Typical timeline
                </span>
                <span className="font-display text-sm text-foreground font-medium">
                  {current.timeline}
                </span>
              </div>
            </div>
            <div>
              <h4 className="font-body text-sm uppercase tracking-[0.2em] text-muted-foreground mb-6">
                What You Receive
              </h4>
              <ul className="space-y-4">
                {current.deliverables.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              {activeStep < journeySteps.length - 1 && (
                <Button
                  variant="luxury"
                  size="lg"
                  className="mt-8"
                  onClick={() => setActiveStep(activeStep + 1)}
                >
                  Next: {journeySteps[activeStep + 1].title}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
              {activeStep === journeySteps.length - 1 && (
                <Button variant="luxury" size="lg" className="mt-8" asChild>
                  <a href="#investment-assessment">
                    Begin Your Journey
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </a>
                </Button>
              )}
            </div>
          </motion.div>

          {/* Mobile: vertical cards */}
          <div className="lg:hidden space-y-4">
            {journeySteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="bg-card border border-border/50 rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <step.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-primary">
                      Step {step.number} · {step.timeline}
                    </p>
                    <h3 className="font-display text-lg text-foreground">{step.title}</h3>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {step.description}
                </p>
                <ul className="space-y-2">
                  {step.deliverables.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-foreground/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mt-16"
        >
          <p className="font-body text-muted-foreground mb-4">
            Every journey begins with a conversation.
          </p>
          <Button variant="luxury" size="xl" asChild>
            <a href="#investment-assessment" className="flex items-center gap-2">
              Start Your Investment Assessment
              <ArrowRight className="h-5 w-5" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ClientJourneySection;
